import type { ReactNode } from "react";

import clsx from "clsx";

import { BaseArrowDownIcon, CheckIcon } from "../Icons";
import { PopoverMenu } from "../PopoverMenu";
import { Typo } from "../Typo";

export type ChipSelectOption<Value extends string = string> = {
  value: Value;
  label: string;
  triggerLabel?: string;
  icon?: ReactNode;
};

export type ChipSelectProps<Value extends string = string> = {
  options: ChipSelectOption<Value>[];
  value: Value;
  onChange: (value: Value) => void;
  label: string;
  menuTitle?: string;
  className?: string;
  place?: "top" | "bottom";
  align?: "start" | "end";
  "data-testid"?: string;
};

export const ChipSelect = <Value extends string = string>({
  options,
  value,
  onChange,
  label,
  menuTitle,
  className,
  place = "bottom",
  align = "start",
  "data-testid": testId,
}: ChipSelectProps<Value>) => {
  const selected = options.find((option) => option.value === value);
  const selectedLabel = selected
    ? (selected.triggerLabel ?? selected.label)
    : "";

  return (
    <PopoverMenu
      asChild
      place={place}
      align={align}
      className={className}
      contentClassName="!rounded-none !bg-transparent !shadow-none"
      content={(close) => (
        <div
          data-testid={testId ? `${testId}-dropdown` : undefined}
          className="flex max-h-80 min-w-[200px] flex-col overflow-y-auto rounded-lg border border-stroke-secondary bg-surface-primary shadow-elevation"
        >
          {menuTitle && (
            <div className="px-4 pt-4">
              <Typo
                element="p"
                type="labelSM"
                textColor="text-text-primary"
                className="truncate"
              >
                {menuTitle}
              </Typo>
            </div>
          )}

          <div className="flex flex-col gap-1 p-4">
            {options.map((option) => {
              const isSelected = option.value === value;
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => {
                    onChange(option.value);
                    close();
                  }}
                  className={clsx(
                    "flex h-10 w-full items-center gap-2 rounded-lg p-2 text-left",
                    isSelected
                      ? "bg-state-selected"
                      : "hover:bg-surface-tonal-default",
                  )}
                >
                  <Typo
                    element="span"
                    type="bodySM"
                    textColor={
                      isSelected ? "text-text-selected" : "text-text-primary"
                    }
                    className="block min-w-0 flex-1 truncate"
                  >
                    {option.label}
                  </Typo>
                  {isSelected && (
                    <CheckIcon
                      aria-hidden="true"
                      focusable="false"
                      className="h-6 w-6 shrink-0 text-icon-selected"
                    />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    >
      <button
        type="button"
        data-testid={testId}
        aria-label={`${label}: ${selectedLabel}`}
        className="flex h-8 w-full max-w-full items-center justify-center rounded-full border border-stroke-primary bg-surface-primary p-1"
      >
        {selected?.icon}
        <span className="flex min-w-0 items-center px-1">
          <Typo
            element="span"
            type="labelSM"
            textColor="text-text-primary"
            className="block truncate text-center"
          >
            {selectedLabel}
          </Typo>
        </span>
        <BaseArrowDownIcon
          aria-hidden="true"
          focusable="false"
          className="h-4 w-4 shrink-0 text-icon-primary"
        />
      </button>
    </PopoverMenu>
  );
};
